import React, { useMemo } from 'react';
import { SplitClient, SplitTreatments } from '@splitsoftware/splitio-react';
import { Icon, Div } from '@pinpoint/design-kit';
import { Ubac } from 'src/containers';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { checkSufficientRole } from 'src/functions';

/* eslint-disable react/prop-types */

function CreateModelFeatureToggleOn({
  client,
  isActuarialModelValid,
  isClassifierModelValid,
  isDeprecatedPlatform,
  meetsValidationMatchedThreshold,
  role,
  showCreateActuarialModal,
  showCreateClassifierModal,
  siteRole,
}) {
  const modelIsValid = isClassifierModelValid || isActuarialModelValid;
  const modelSufficientBlindValidation = isActuarialModelValid && meetsValidationMatchedThreshold;

  const roleIsSufficient =
    (checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_USER, true) &&
      checkSufficientRole(role, CONSTANTS.ROLE.ADMIN, false)) ||
    checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_SERVICES, true);
  const isSuperAdmin = siteRole === CONSTANTS.ROLE.SUPER_ADMIN;
  let tooltip;
  let disabled = false;
  if (isDeprecatedPlatform) {
    // Check first because it short-circuits all other tooltips
    tooltip = 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    disabled = true;
  } else if (!modelIsValid) {
    // Next, if the selected segments aren't valid for the model types, then end here
    tooltip = 'Select ACTUARIAL, or HIT + MISS (+ VALIDATION)';
    disabled = true;
  } else if (!roleIsSufficient) {
    // must be at least an admin in membership or client services site role
    tooltip = 'You do not have sufficient permission to create a new model';
    disabled = true;
  } else if (!isSuperAdmin && !modelSufficientBlindValidation && !isClassifierModelValid) {
    // when actuarial model, it needs sufficient blind validation records UNLESS you are a super admin
    tooltip =
      'This model does not contain sufficient blind-validation records. Please request approval to train this model ' +
      'using an alternative validation method.';
    disabled = true;
  } else {
    tooltip = 'Create a new predictive model';
  }

  if (!client?.canModel)
    return (
      <Div data-test="new-model-btn-div">
        <Button
          buttonStyle="primary"
          disabled
          onClick={() => null}
          data-tip="Select ACTUARIAL, or HIT + MISS (+ VALIDATION)"
        >
          <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
          NEW MODEL
        </Button>
      </Div>
    );

  return (
    <Div data-test="new-model-btn-div">
      <Button
        buttonStyle="primary"
        disabled={disabled}
        onClick={() => {
          if (isActuarialModelValid) showCreateActuarialModal(true);
          else if (isClassifierModelValid) showCreateClassifierModal(true);
        }}
        data-tip={tooltip}
      >
        <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
        NEW MODEL
      </Button>
    </Div>
  );
}

function CreateModelFeatureToggleOff({
  client,
  isActuarialModelValid,
  isClassifierModelValid,
  isDeprecatedPlatform,
  meetsValidationMatchedThreshold,
  role,
  showCreateActuarialModal,
  showCreateClassifierModal,
  siteRole,
}) {
  const modelIsValid = isClassifierModelValid || isActuarialModelValid;
  const modelSufficientBlindValidation =
    !isClassifierModelValid && isActuarialModelValid && meetsValidationMatchedThreshold;
  const roleIsSufficient =
    checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_SERVICES, true) ||
    checkSufficientRole(role, CONSTANTS.ROLE.ADMIN, false);
  const isSuperAdmin = siteRole === CONSTANTS.ROLE.SUPER_ADMIN;
  let tooltip;
  let disabled = false;
  if (isDeprecatedPlatform) {
    // Check first because it short-circuits all other tooltips
    tooltip = 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    disabled = true;
  } else if (!modelIsValid) {
    // Next, if the selected segments aren't valid for the model types, then end here
    tooltip = 'Select ACTUARIAL, or HIT + MISS (+ VALIDATION)';
    disabled = true;
  } else if (!roleIsSufficient) {
    // must be at least an admin in membership or client services site role
    tooltip = 'You do not have sufficient permission to create a new model';
    disabled = true;
  } else if (!isSuperAdmin && !modelSufficientBlindValidation) {
    // when actuarial model, it needs sufficient blind validation records UNLESS you are a super admin
    tooltip =
      'This model does not contain sufficient blind-validation records. Please request approval to train this model ' +
      'using an alternative validation method.';
    disabled = true;
  } else {
    tooltip = 'Create a new predictive model';
  }

  if (!client?.canModel) return null;

  return (
    <Ubac users={CONSTANTS.ENG_TEAM}>
      <Button
        buttonStyle="primary"
        disabled={disabled}
        data-tip={tooltip}
        onClick={() => {
          if (isClassifierModelValid) showCreateClassifierModal(true);
          if (isActuarialModelValid) showCreateActuarialModal(true);
        }}
      >
        <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
        NEW MODEL
      </Button>
    </Ubac>
  );
}

export function CreateModelFeatureToggle({ tenant, ...rest }) {
  return (
    <SplitClient splitKey={tenant}>
      <SplitTreatments names={[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]}>
        {({ treatments, isReady }) => {
          if (!isReady) {
            return null;
          }
          if (treatments[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS].treatment === 'on') {
            return <CreateModelFeatureToggleOn {...rest} />;
          }
          return <CreateModelFeatureToggleOff {...rest} />;
        }}
      </SplitTreatments>
    </SplitClient>
  );
}

function PredictModelFeatureToggleOn({ client, isPredictModelValid, showPredictModel, isAdmin, isDeprecatedPlatform }) {
  const disabled = !isPredictModelValid || !isAdmin || isDeprecatedPlatform;
  const tooltip = useMemo(() => {
    if (disabled && (!isPredictModelValid || !isAdmin)) {
      return 'Select TARGET';
    }
    if (disabled && isDeprecatedPlatform) {
      return 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    }
    return 'Write out predictions to dataset';
  }, [disabled, isPredictModelValid, isAdmin, isDeprecatedPlatform]);

  return client && client.canScore ? (
    <Div data-test="predict-btn-div">
      <Button
        buttonStyle="primary"
        disabled={disabled}
        data-tip={tooltip}
        onClick={() => {
          showPredictModel(true);
        }}
      >
        <Icon name="ThinkalikeModel" color="white" m={{ r: '0.5rem' }} size="1rem" />
        PREDICT
      </Button>
    </Div>
  ) : null;
}

function PredictModelFeatureToggleOff({ client, isPredictModelValid, showPredictModel, isAdmin }) {
  const disabled = !isPredictModelValid || !isAdmin;
  return client && client.canScore ? (
    <Ubac users={CONSTANTS.ENG_TEAM}>
      <Div data-test="predict-btn-div">
        <Button
          buttonStyle="primary"
          disabled={disabled}
          data-tip={disabled ? 'Select TARGET' : 'Write out predictions to dataset'}
          onClick={() => {
            showPredictModel(true);
          }}
        >
          <Icon name="ThinkalikeModel" color="white" m={{ r: '0.5rem' }} size="1rem" />
          PREDICT
        </Button>
      </Div>
    </Ubac>
  ) : null;
}

export function PredictModelFeatureToggle({ tenant, ...rest }) {
  return (
    <SplitClient splitKey={tenant}>
      <SplitTreatments names={[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]}>
        {({ treatments, isReady }) => {
          if (!isReady) {
            return null;
          }
          if (treatments[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS].treatment === 'on') {
            return <PredictModelFeatureToggleOn {...rest} />;
          }
          return <PredictModelFeatureToggleOff {...rest} />;
        }}
      </SplitTreatments>
    </SplitClient>
  );
}
